import React, { useState, useEffect, useRef } from 'react';
import { apiHtml } from '../data/calcHtml';

// Decode base64 HTML and create a blob URL for the iframe
function makeUrl(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

const CALCULATORS = [
  {
    id: 'api',
    label: 'API Gravity & Energy Density',
    icon: '⛽',
    b64: apiHtml,
    desc: 'API gravity, specific gravity, energy density (BTU/lb, BTU/gal, MJ/kg) from density at any temperature.',
  },
  {
    id: 'visc',
    label: 'Viscosity · Density · Cetane Index',
    icon: '🧪',
    path: '/calculators/diesel-properties-30-samples.html',
    desc: 'Corrected 30-sample diesel worksheet for viscosity, density, API gravity, cetane index, sulfur, flash point, and D524 residue.',
  },
  {
    id: 'temperature',
    label: 'Temperature Conversion',
    icon: '🌡️',
    path: '/calculators/temperature-conversion.html',
    desc: 'Convert laboratory temperatures from Celsius to Fahrenheit for multiple samples.',
  },
  {
    id: 'moisture-ash',
    label: 'Moisture & Ash',
    icon: '⚖️',
    path: '/calculators/moisture-ash.html',
    desc: 'Calculate gravimetric moisture and ash percentages from tare, total, and final weights.',
  },
  {
    id: 'soil-loi',
    label: 'Soil Moisture & Organic Matter',
    icon: '🌱',
    path: '/calculators/soil-moisture-organic-matter.html',
    desc: 'Calculate soil moisture and organic matter by loss on ignition using paired gravimetric weights.',
  },
  {
    id: 'water-percent',
    label: 'Water Percentage',
    icon: '💧',
    path: '/calculators/water-percent.html',
    desc: 'Calculate water percentage from container tare, initial total weight, and final dry weight.',
  },
  {
    id: 'tss',
    label: 'TSS — 20 Samples',
    icon: '🧫',
    path: '/calculators/tss-20-samples.html',
    desc: 'Calculate total suspended solids in mg/L from filter weights and sample volume for up to 20 samples.',
  },
  {
    id: 'particulate-01',
    label: '0.1 µm Particulate',
    icon: '🔬',
    path: '/calculators/particulate-0-1-micron.html',
    desc: 'Calculate particulate retained on a 0.1 µm membrane with method-blank correction and QC flags.',
  },
  {
    id: 'proximate',
    label: 'Proximate Analysis',
    icon: '🔥',
    path: '/calculators/proximate-analysis.html',
    desc: 'Calculate moisture, volatile matter, ash, and fixed carbon on as-received, dry, and dry ash-free bases.',
  },
  {
    id: 'd2276',
    label: 'Diesel Particulate — D2276',
    icon: '⛽',
    path: '/calculators/diesel-particulate-d2276.html',
    desc: 'ASTM D2276 diesel-fuel particulate batch worksheet with blank correction and mg/1000 mL reporting.',
  },
];

export default function Calculators() {
  const [active, setActive] = useState('api');
  const [urls, setUrls] = useState({});
  const urlsRef = useRef({});

  // Create blob URLs once on mount
  useEffect(() => {
    const created = {};
    const generated = [];
    CALCULATORS.forEach(c => {
      created[c.id] = c.b64 ? makeUrl(c.b64) : c.path;
      if (c.b64) generated.push(created[c.id]);
    });
    urlsRef.current = created;
    setUrls(created);
    // Cleanup on unmount
    return () => generated.forEach(u => URL.revokeObjectURL(u));
  }, []);

  const calc = CALCULATORS.find(c => c.id === active);

  return (
    <div style={{display:'flex', flexDirection:'column', flex:1, overflow:'hidden', minHeight:0}}>

      {/* Tab bar */}
      <div style={{background:'white', borderBottom:'2px solid #e0e4ec',
        padding:'0 12px', display:'flex', gap:0, flexShrink:0, alignItems:'center', overflowX:'auto'}}>
        <span style={{fontSize:11, fontWeight:700, color:'#7788aa',
          textTransform:'uppercase', letterSpacing:'0.5px', marginRight:16}}>
          🔢 Calculators
        </span>
        {CALCULATORS.map(c => (
          <button key={c.id} onClick={() => setActive(c.id)}
            style={{padding:'10px 18px', border:'none', background:'none',
              cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap',
              color: active===c.id ? '#1e3060' : '#7788aa',
              borderBottom: active===c.id ? '3px solid #1e3060' : '3px solid transparent',
              display:'flex', alignItems:'center', gap:6}}>
            <span>{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>

      {/* Description bar */}
      <div style={{background:'#f8f9ff', borderBottom:'1px solid #e0e4ec',
        padding:'6px 16px', flexShrink:0, display:'flex', alignItems:'center', gap:10}}>
        <span style={{fontSize:12, color:'#555'}}>{calc?.desc}</span>
        {urls[active] && (
          <a href={urls[active]} target="_blank" rel="noreferrer"
            style={{marginLeft:'auto', fontSize:11, color:'#1a7dc4',
              textDecoration:'none', whiteSpace:'nowrap', flexShrink:0,
              border:'1px solid #aac8ee', borderRadius:4,
              padding:'3px 10px', background:'white'}}>
            ↗ Open in new tab
          </a>
        )}
      </div>

      {/* Iframes — all mounted, only active one visible */}
      <div style={{flex:1, position:'relative', overflow:'hidden'}}>
        {CALCULATORS.map(c => (
          <iframe
            key={c.id}
            src={urls[c.id] || 'about:blank'}
            title={c.label}
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: active === c.id ? 'block' : 'none',
            }}
          />
        ))}
        {!urls[active] && (
          <div style={{display:'flex', alignItems:'center', justifyContent:'center',
            height:'100%', color:'#999', fontSize:14}}>
            ⏳ Loading calculator...
          </div>
        )}
      </div>
    </div>
  );
}
